/**
 * Contact lifecycle status — single source of truth for labels, colors,
 * order, and confirmation rules. Locked 2026-05-31 (post-port audit
 * items 10 + 11 + centralization round).
 *
 * Naming policy:
 *   - DB enum values + status type tokens use `new_lead | qualified | purchase`
 *   - UI display matches the token-with-space ("New Lead", "Purchase")
 *   - Conversational copy can say "sale" / "won sale" when natural
 *     (same dual-vocab pattern as contact/customer for marketing copy)
 *
 * Why "Purchase" for the won state: matches the Google Ads + Meta CAPI
 * `Purchase` event the backend already fires for this transition. One
 * word across the whole product stack — dashboard, backend service,
 * platform event chain.
 *
 * Why "New Lead" for the new state: the CONTACT (table entity) might be
 * a returning person; the LEAD (signal of fresh interest) is always new.
 * Brand-voice carve-out: "lead" is allowed in in-product status labels
 * (not marketing copy — marketing still uses "contact" / "customer").
 *
 * Adding a new status:
 *   1. Add to ContactStatus union in src/types/contacts.ts
 *   2. Add entries in STATUS_LABELS / STATUS_DOT_COLORS / STATUS_TEXT_COLORS
 *   3. Append to STATUS_ORDER (drives Select + dropdown menus)
 *   4. If it gates a value-entry Dialog, add to REQUIRES_VALUE
 *
 * Do NOT add status labels inline anywhere. If you find a hardcoded
 * "Purchase" / "New Lead" / "Mark as ..." string in the codebase, it's
 * a bug — replace it with the helpers below.
 */

import type { ContactStatus } from '@/types/contacts';

// Lifecycle order — pickers, dropdowns, and the StatusPipeline Select
// render in this sequence.
export const STATUS_ORDER: ContactStatus[] = [
  'new_lead',
  'qualified',
  'purchase',
];

// Display labels. Match brand-sheet §3 status word patterns (Title-case,
// no glyph). Conversational copy elsewhere may use "sale" / "won sale".
export const STATUS_LABELS: Record<ContactStatus, string> = {
  new_lead: 'New Lead',
  qualified: 'Qualified',
  purchase: 'Purchase',
};

// Status dot — small colored circle next to the label.
// Slate for neutral new, info-blue for qualified, green-500 for the
// celebratory purchase moment. Per brand-sheet §1 status palette.
export const STATUS_DOT_COLORS: Record<ContactStatus, string> = {
  new_lead: 'bg-slate-400',
  qualified: 'bg-info',
  purchase: 'bg-green-500',
};

// Label text color (used in dashboard recent-activity rows + ContactsPage
// inline status display). Per brand-sheet §1 status palette.
export const STATUS_TEXT_COLORS: Record<ContactStatus, string> = {
  new_lead: 'text-slate-700',
  qualified: 'text-info',
  purchase: 'text-green-700',
};

// Status transitions that require a value-entry Dialog (currency input).
// `purchase` collects sale value to fire Purchase + revenue back to the
// ad platforms.
export const REQUIRES_VALUE: ContactStatus[] = ['purchase'];

// Status transitions that pop a confirm-before-fire AlertDialog (because
// they trigger a server-side conversion fire to Google Ads / Meta CAPI).
export const REQUIRES_CONFIRM: ContactStatus[] = ['qualified', 'purchase'];

export function statusBadge(s: ContactStatus): { dot: string; text: string } {
  return { dot: STATUS_DOT_COLORS[s], text: STATUS_TEXT_COLORS[s] };
}

// "Mark as Purchase" / "Mark as Qualified" — used as Dialog / AlertDialog
// titles when collecting confirmation for a transition.
export function markAsLabel(s: ContactStatus): string {
  return `Mark as ${STATUS_LABELS[s]}`;
}
